/**
 * pipMessageZIndex.ts
 *
 * Finds StoryBlock PiP message node and normalizes parent z-index when player is minimized.
 */

/**
 * Queries selector across document and nested open shadow roots.
 */
function querySelectorIncludingOpenShadowRoots(
  selector: string,
  root: ParentNode = document
): Element | null {
  const directMatch = root.querySelector(selector);
  if (directMatch) return directMatch;

  const elements = root.querySelectorAll("*");
  for (const element of elements) {
    const shadowRoot = (element as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot;
    if (!shadowRoot) continue;

    const nestedMatch = querySelectorIncludingOpenShadowRoots(selector, shadowRoot);
    if (nestedMatch) return nestedMatch;
  }

  return null;
}

/**
 * Finds StoryBlock host element, preferring card-scoped lookup when encoded id is available.
 */
function findStoryBlockElement(encodedId: string | undefined) {
  const cardRoot = encodedId ? document.getElementById(encodedId) : null;

  if (cardRoot) {
    const cardScopedSelectors: string[] = [
      `fw-storyblock[name="player-${encodedId}"]`,
      `fw-storyblock[video="${encodedId}"]`,
      'fw-storyblock[data-testid="video-player"]',
      "fw-storyblock",
    ];

    for (const selector of cardScopedSelectors) {
      const found = querySelectorIncludingOpenShadowRoots(selector, cardRoot) as
        (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
      if (found) return found;
    }
  }

  const selectors: string[] = [
    ...(encodedId
      ? [`fw-storyblock[name="player-${encodedId}"]`, `fw-storyblock[video="${encodedId}"]`]
      : []),
    'fw-storyblock[data-testid="video-player"]',
    "fw-storyblock",
  ];

  for (const selector of selectors) {
    const found = querySelectorIncludingOpenShadowRoots(selector) as
      (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
    if (found) return found;
  }

  return null;
}

/**
 * Applies z-index fix for PiP message container with bounded retries.
 */
export function setStoryBlockPipMessageZIndex(encodedId: string | undefined, maxRetries = 8) {
  const trySetZIndex = (attempt: number) => {
    const storyBlock = findStoryBlockElement(encodedId);
    const pipMessage = storyBlock?.shadowRoot?.querySelector(
      ".cy-video-in-pip-message"
    ) as HTMLElement | null;

    if (!pipMessage?.parentElement) {
      if (attempt < maxRetries) {
        requestAnimationFrame(() => {
          trySetZIndex(attempt + 1);
        });
      }
      return;
    }

    pipMessage.parentElement.style.zIndex = "4";
  };

  trySetZIndex(0);
}
